/**
 * Forensic Shadow Audit - premium PDF delivery after unlock.
 */

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

/** sessionStorage key: pending audit doc id for POST /unlock-deliver-pdf */
export const PENDING_FORENSIC_AUDIT_PDF_KEY =
  "clumse_pending_forensic_audit_pdf";

/** Call when an audit_id is known (e.g. after POST /forensic-audit/generate). */
export function rememberForensicAuditForPdfDelivery(auditId) {
  if (!auditId || typeof auditId !== "string") return;
  try {
    sessionStorage.setItem(PENDING_FORENSIC_AUDIT_PDF_KEY, auditId.trim());
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/** If the landing URL includes ?source=forensic_audit&audit_id=…, persist for post-premium delivery. */
export function captureForensicAuditIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("source");
    const aid = params.get("audit_id");
    if (src === "forensic_audit" && aid && /^[\w-]+$/i.test(aid)) {
      rememberForensicAuditForPdfDelivery(aid);
    }
  } catch (_) {
    /* ignore */
  }
}

/** Dedupe concurrent deliveries for the same audit id (e.g. React Strict Mode). */
const deliverPromises = new Map();

/**
 * Premium users only (backend enforces).
 * @returns {Promise<{ download_url?: string, success?: boolean }>}
 */
export function unlockDeliverForensicAuditPdfOnce(auditId) {
  if (!API_URL) {
    return Promise.reject(new Error("API URL not configured"));
  }
  if (!auditId || typeof auditId !== "string") {
    return Promise.reject(new Error("audit_id is required"));
  }
  const key = auditId.trim();
  const existing = deliverPromises.get(key);
  if (existing) return existing;

  const p = (async () => {
    try {
      const auth = await import("firebase/auth");
      const { getAuth } = auth;
      const user = getAuth().currentUser;
      if (!user) {
        throw new Error("You must be signed in");
      }
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/forensic-audit/unlock-deliver-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ audit_id: key }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `PDF delivery failed (${res.status})`);
      }
      return { download_url: data.download_url, success: true };
    } finally {
      deliverPromises.delete(key);
    }
  })();

  deliverPromises.set(key, p);
  return p;
}
